/**
 * Resume Studio — On-site social share widget.
 *
 * Shown on the paid success screen. 5 buttons (WhatsApp / LinkedIn /
 * Twitter / Facebook / Copy Link) carry a referral URL that hooks into
 * the "Refer 5 friends → earn ₹49 back" cashback loop (parked feature).
 */
import React, { useState } from "react";
import {
    ShareNetwork, WhatsappLogo, LinkedinLogo, TwitterLogo,
    FacebookLogo, Copy,
} from "@phosphor-icons/react";

export default function ShareCVWidget({ session }) {
    const [copied, setCopied] = useState(false);
    const [copyFailed, setCopyFailed] = useState(false);
    const refCode = session?.session_id || "friend";
    const shareUrl = `${window.location.origin}/resume?ref=${refCode}&utm_source=share&utm_medium=peer`;
    const caption = "I just built an ATS-cracking CV in 90 seconds with NaukriDaddy Resume Studio (NaukriDaddy AI does the heavy lifting). Serious upgrade — try it 👇";

    const enc = encodeURIComponent;
    const links = {
        whatsapp: `https://wa.me/?text=${enc(caption + "\n\n" + shareUrl)}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`,
        twitter: `https://twitter.com/intent/tweet?text=${enc(caption)}&url=${enc(shareUrl)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}&quote=${enc(caption)}`,
    };

    const copyLink = async () => {
        setCopyFailed(false);
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            setCopyFailed(true);
            setTimeout(() => setCopyFailed(false), 2500);
        }
    };

    return (
        <div
            data-testid="share-cv-widget"
            className="mt-6 rounded-xl border border-slate-200 bg-gradient-to-br from-white to-cobalt-50/40 p-5"
        >
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-cobalt-500 text-white grid place-items-center">
                    <ShareNetwork weight="fill" size={18} />
                </div>
                <div className="flex-1">
                    <div className="text-sm font-bold">Share Resume Studio with 5 friends → earn ₹49 back</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                        Every friend who buys a CV counts. Payout via UPI when 5 friends complete.
                    </div>
                </div>
            </div>
            <div className="mt-4 grid grid-cols-5 gap-2">
                <a href={links.whatsapp} target="_blank" rel="noopener noreferrer"
                   data-testid="share-whatsapp-btn"
                   className="flex flex-col items-center gap-1 border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/60 rounded-lg py-2 transition">
                    <WhatsappLogo weight="fill" size={22} className="text-emerald-600" />
                    <span className="text-[10px] font-semibold text-slate-700">WhatsApp</span>
                </a>
                <a href={links.linkedin} target="_blank" rel="noopener noreferrer"
                   data-testid="share-linkedin-btn"
                   className="flex flex-col items-center gap-1 border border-slate-200 hover:border-blue-600 hover:bg-blue-50/60 rounded-lg py-2 transition">
                    <LinkedinLogo weight="fill" size={22} className="text-[#0A66C2]" />
                    <span className="text-[10px] font-semibold text-slate-700">LinkedIn</span>
                </a>
                <a href={links.twitter} target="_blank" rel="noopener noreferrer"
                   data-testid="share-twitter-btn"
                   className="flex flex-col items-center gap-1 border border-slate-200 hover:border-sky-500 hover:bg-sky-50/60 rounded-lg py-2 transition">
                    <TwitterLogo weight="fill" size={22} className="text-sky-500" />
                    <span className="text-[10px] font-semibold text-slate-700">Twitter</span>
                </a>
                <a href={links.facebook} target="_blank" rel="noopener noreferrer"
                   data-testid="share-facebook-btn"
                   className="flex flex-col items-center gap-1 border border-slate-200 hover:border-blue-700 hover:bg-blue-50/60 rounded-lg py-2 transition">
                    <FacebookLogo weight="fill" size={22} className="text-[#1877F2]" />
                    <span className="text-[10px] font-semibold text-slate-700">Facebook</span>
                </a>
                <button type="button" onClick={copyLink}
                        data-testid="share-copy-btn"
                        className="flex flex-col items-center gap-1 border border-slate-200 hover:border-cobalt-500 hover:bg-cobalt-50/60 rounded-lg py-2 transition">
                    <Copy weight="fill" size={22} className={copied ? "text-emerald-600" : copyFailed ? "text-amber-600" : "text-slate-500"} />
                    <span className="text-[10px] font-semibold text-slate-700">
                        {copied ? "Copied!" : copyFailed ? "Use Ctrl+C" : "Copy link"}
                    </span>
                </button>
            </div>
        </div>
    );
}
